<template>
  <div>
    <app-header />

    <div class="container-fluid mt-4 mb-4 px-5">
      <div class="row m-t-xl m-b-md">
        <div class="col-sm-6">
          <h1 class="title">Support Dashboard</h1>
          <p v-if="lastRefreshed" class="text-muted small">Data fetched from Freshdesk at {{ lastRefreshed }}</p>
        </div>
        <div class="col-sm-6 text-end">
          <button class="btn btn-primary" :disabled="loading" @click="refreshData">
            <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
            Refresh
          </button>
        </div>
      </div>

      <div class="row mb-4">
        <div v-for="widget in countWidgets" :key="widget.key" class="col-md-6 col-xl mb-3">
          <div class="card card-widget">
            <div class="card-body">
              <h3 class="widget-title">{{ widget.label }}</h3>
              <p
                class="lead strong"
                :class="{ 'text-danger': !loading && widget.alert !== null && counts[widget.key] > widget.alert }"
              >{{ loading ? '—' : counts[widget.key] }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="decorative-divider m-b-md"></div>

      <div class="row mb-3 align-items-center">
        <div class="col-md-4 col-lg-3">
          <label class="form-label fw-semibold">Agent</label>
          <multiselect
            v-model="selectedAgent"
            :options="agentList"
            :show-labels="false"
            :allow-empty="true"
            placeholder="All Agents"
            label="agent_name"
            track-by="responder_id"
          />
        </div>
        <div v-if="selectedAgentStats" class="col align-self-end pb-1">
          <span class="text-muted small">{{ selectedAgentStats.total }} ticket{{ selectedAgentStats.total !== 1 ? 's' : '' }} total</span>
        </div>
      </div>

      <div v-if="!loading && agentBreakdown.length" class="row mb-4">
        <div
          v-for="item in allStatusBreakdown"
          :key="item.statusId"
          class="col-md-2 mb-3"
        >
          <div class="card card-widget" :class="{ 'opacity-50': item.count === 0 }">
            <div class="card-body">
              <div><span class="badge" :style="statusBadgeStyle(item.statusId)">{{ item.label }}</span></div>
              <p class="lead strong mt-2">{{ item.count }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="decorative-divider m-b-md"></div>

      <div class="row mb-2 align-items-center">
        <div class="col">
          <h2>Client Ticket Breakdown</h2>
        </div>
      </div>

      <div v-if="!loading" class="card ctb mb-4">
        <div class="ctb-topbar">
          <div class="ctb-legend">
            <div v-for="col in ctbCols" :key="col.statusId" class="ctb-legend-item">
              <div class="ctb-legend-dot" :style="{ background: col.color }"></div>
              {{ col.label }}
            </div>
          </div>
          <div class="ctb-search-wrap">
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="#979797" stroke-width="2" stroke-linecap="round">
              <circle cx="6.5" cy="6.5" r="5.5"/><line x1="10.5" y1="10.5" x2="14" y2="14"/>
            </svg>
            <input v-model="clientSearch" class="ctb-search" type="text" placeholder="Filter clients…" />
          </div>
        </div>

        <div class="ctb-scroll">
          <table class="ctb-table">
            <thead>
              <tr>
                <th :class="{ sorted: clientSortKey === 'company_name' }" @click="ctbSort('company_name')">
                  Client <span class="sort-icon">▼</span>
                </th>
                <th>Breakdown</th>
                <th v-for="col in ctbCols" :key="col.statusId">{{ col.label }}</th>
                <th :class="{ sorted: clientSortKey === 'total' }" @click="ctbSort('total')">
                  Total <span class="sort-icon">▼</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!ctbRows.length">
                <td :colspan="ctbCols.length + 3" class="ctb-empty">No clients match your search.</td>
              </tr>
              <tr v-for="row in ctbRows" :key="row.company_name">
                <td class="ctb-client-name">{{ row.company_name }}</td>
                <td>
                  <div class="ctb-bar">
                    <div
                      v-for="(seg, i) in row.barSegments"
                      :key="i"
                      class="ctb-bar-seg"
                      :style="{ width: seg.width + '%', background: seg.color }"
                    ></div>
                  </div>
                </td>
                <td v-for="col in ctbCols" :key="col.statusId">
                  <span v-if="row[col.statusId] > 0" class="ctb-badge" :style="statusBadgeStyle(col.statusId)">{{ row[col.statusId] }}</span>
                  <span v-else class="ctb-badge-nil">0</span>
                </td>
                <td class="ctb-total">{{ row.total }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="decorative-divider m-b-md"></div>

      <div class="row mb-2">
        <div class="col">
          <h2>Overdue Next Response</h2>
        </div>
      </div>

      <c-data-list v-if="!loading" :items="filteredOverdueNextResponse">
        <template v-slot:columns>
          <div class="col-2 col-lg-1">Ticket #</div>
          <div class="col-12 col-lg-4">Name</div>
          <div class="col-6 col-lg-2">Client</div>
          <div class="col-6 col-lg-2">Status</div>
          <div class="col-6 col-lg-1">First Response Due</div>
          <div class="col-6 col-lg-1 text-end">Priority</div>
        </template>
        <template v-slot:default="slotProps">
          <a :href="slotProps.item.url" target="_blank">
            <div class="row">
              <div class="col-2 col-lg-1">
                <p class="ticket-id">#{{ slotProps.item.id }}</p>
              </div>
              <div class="col-12 col-lg-4">
                <p class="text-truncate">
                  <span class="data-label d-lg-none">Ticket:</span>
                  <span class="strong">{{ slotProps.item.subject }}</span>
                </p>
              </div>
              <div class="col-6 col-lg-2">
                <p class="text-truncate">
                  <span class="data-label d-lg-none">Client:</span>
                  {{ slotProps.item.company_name || 'Unknown' }}
                </p>
              </div>
              <div class="col-6 col-lg-2">
                <span class="data-label d-lg-none">Status:</span>
                <span class="badge" :style="statusBadgeStyle(slotProps.item.status)">{{ statusLabel(slotProps.item.status) }}</span>
              </div>
              <div class="col-6 col-lg-1 text-danger">
                <span class="data-label d-lg-none">First Response Due:</span>
                <p class="text-truncate">{{ slotProps.item.fr_due_by | moment('DD/MM/YYYY h:mm A') }}</p>
              </div>
              <div class="col-6 col-lg-1 text-end">
                <span class="data-label d-lg-none">Priority:</span>
                <span :class="'badge rounded-pill ' + priorityBadgeClass(slotProps.item.priority)">{{ priorityLabel(slotProps.item.priority) }}</span>
              </div>
            </div>
          </a>
        </template>
      </c-data-list>

      <div class="decorative-divider m-b-md"></div>

      <div class="row mb-2">
        <div class="col">
          <h2>Overdue Resolution</h2>
        </div>
      </div>

      <c-data-list v-if="!loading" :items="filteredOverdueResolution">
        <template v-slot:columns>
          <div class="col-2 col-lg-1">Ticket #</div>
          <div class="col-12 col-lg-4">Name</div>
          <div class="col-6 col-lg-2">Client</div>
          <div class="col-6 col-lg-2">Status</div>
          <div class="col-6 col-lg-1">Resolution Due</div>
          <div class="col-6 col-lg-1 text-end">Priority</div>
        </template>
        <template v-slot:default="slotProps">
          <a :href="slotProps.item.url" target="_blank">
            <div class="row">
              <div class="col-2 col-lg-1">
                <p class="ticket-id">#{{ slotProps.item.id }}</p>
              </div>
              <div class="col-12 col-lg-4">
                <p class="text-truncate">
                  <span class="data-label d-lg-none">Ticket:</span>
                  <span class="strong">{{ slotProps.item.subject }}</span>
                </p>
              </div>
              <div class="col-6 col-lg-2">
                <p class="text-truncate">
                  <span class="data-label d-lg-none">Client:</span>
                  {{ slotProps.item.company_name || 'Unknown' }}
                </p>
              </div>
              <div class="col-6 col-lg-2">
                <span class="data-label d-lg-none">Status:</span>
                <span class="badge" :style="statusBadgeStyle(slotProps.item.status)">{{ statusLabel(slotProps.item.status) }}</span>
              </div>
              <div class="col-6 col-lg-1 text-danger">
                <span class="data-label d-lg-none">Resolution Due:</span>
                <p class="text-truncate">{{ slotProps.item.due_by | moment('DD/MM/YYYY h:mm A') }}</p>
              </div>
              <div class="col-6 col-lg-1 text-end">
                <span class="data-label d-lg-none">Priority:</span>
                <span :class="'badge rounded-pill ' + priorityBadgeClass(slotProps.item.priority)">{{ priorityLabel(slotProps.item.priority) }}</span>
              </div>
            </div>
          </a>
        </template>
      </c-data-list>
    </div>
  </div>
</template>

<script>
  import { mapActions } from 'vuex'
  import moment from 'moment'
  import { notify } from 'utils'
  import AppConstants from '@/support/app-constants'

  const STATUS_LABELS = {
    2: 'Open',
    3: 'Pending',
    5: 'Closed',
    4: 'Resolved',
    6: 'Waiting on Customer',
    11: 'Waiting for Sign-off',
    14: 'Waiting for Scoping',
    15: 'In Backlog',
    16: 'Pending Deploy',
    17: 'Pending Staging Deploy',
    18: 'Escalated',
    19: 'SLA Hours Exhausted'
  }

  const PRIORITY_LABELS = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Urgent' }
  const PRIORITY_CLASSES = { 1: 'status-mute', 2: 'status-info', 3: 'status-warning', 4: 'status-danger' }

  const STATUS_COLORS = AppConstants.ticket_status_colors

  function hexToRgba (hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r},${g},${b},${alpha})`
  }

  const CTB_COLS = [
    { statusId: 2,  label: 'Open',            color: STATUS_COLORS[2] },
    { statusId: 3,  label: 'Pending',          color: STATUS_COLORS[3] },
    { statusId: 6,  label: 'Waiting Cust.',    color: STATUS_COLORS[6] },
    { statusId: 11, label: 'Waiting Sign-off', color: STATUS_COLORS[11] },
    { statusId: 16, label: 'Pending Deploy',   color: STATUS_COLORS[16] },
    { statusId: 18, label: 'Escalated',        color: STATUS_COLORS[18] },
    { statusId: 19, label: 'SLA Exhausted',    color: STATUS_COLORS[19] },
  ]

  export default {
    data () {
      return {
        loading: false,
        selectedAgent: null,
        counts: {
          pending_deploy: 0,
          unassigned: 0,
          overdue_resolution: 0,
          waiting_on_customer: 0,
          escalated: 0,
        },
        overdueNextResponse: [],
        overdueResolution: [],
        agentBreakdown: [],
        clientBreakdown: [],
        clientSearch: '',
        clientSortKey: 'total',
        clientSortDir: -1,
        ctbCols: CTB_COLS,
        lastRefreshed: null,
        countWidgets: [
          { key: 'pending_deploy', label: 'Pending Deploy', alert: 10 },
          { key: 'unassigned', label: 'Unassigned', alert: 5 },
          { key: 'unclassified', label: 'Unclassified', alert: 5 },
          { key: 'overdue_resolution', label: 'Overdue Resolutions', alert: 10 },
        ],
      }
    },

    computed: {
      agentList () {
        return this.agentBreakdown
      },

      selectedAgentStats () {
        return this.selectedAgent || null
      },

      statusBreakdown () {
        if (this.selectedAgent) {
          return this.selectedAgent.by_status
        }
        return this.agentBreakdown.reduce((acc, agent) => {
          Object.entries(agent.by_status).forEach(([status, count]) => {
            acc[status] = (acc[status] || 0) + count
          })
          return acc
        }, {})
      },

      allStatusBreakdown () {
        const breakdown = this.statusBreakdown
        return Object.entries(STATUS_LABELS).map(([status, label]) => ({
          statusId: parseInt(status),
          label,
          count: breakdown[status] || 0
        }))
      },

      ctbRows () {
        return this.clientBreakdown
          .map(client => {
            const row = { company_name: client.company_name, total: client.total }
            CTB_COLS.forEach(col => {
              row[col.statusId] = client.by_status[col.statusId] || 0
            })
            const barTotal = CTB_COLS.reduce((s, col) => s + (row[col.statusId] || 0), 0) || 1
            row.barSegments = CTB_COLS
              .filter(col => row[col.statusId] > 0)
              .map(col => ({ color: col.color, width: (row[col.statusId] / barTotal * 100).toFixed(1) }))
            return row
          })
          .filter(row => row.company_name.toLowerCase().includes(this.clientSearch.toLowerCase()))
          .sort((a, b) => {
            const av = a[this.clientSortKey]
            const bv = b[this.clientSortKey]
            return this.clientSortDir * (av < bv ? -1 : av > bv ? 1 : 0)
          })
      },

      filteredOverdueNextResponse () {
        if (!this.selectedAgent) return this.overdueNextResponse
        return this.overdueNextResponse.filter(t => this.ticketMatchesAgent(t))
      },

      filteredOverdueResolution () {
        if (!this.selectedAgent) return this.overdueResolution
        return this.overdueResolution.filter(t => this.ticketMatchesAgent(t))
      },
    },

    created () {
      this.fetchData()
    },

    beforeDestroy () {
      clearInterval(this.refreshTimer)
    },

    methods: {
      ...mapActions('supportDashboard', ['getDashboardData']),

      ticketMatchesAgent (ticket) {
        const agentId = this.selectedAgent.responder_id
        if (agentId === null) return !ticket.responder_id
        return String(ticket.responder_id) === String(agentId)
      },

      fetchData ({ refresh = false } = {}) {
        this.loading = true
        this.getDashboardData({ refresh }).then((response) => {
          const data = response.data.data
          this.counts = data.counts
          this.agentBreakdown = data.agent_breakdown
          this.clientBreakdown = data.client_breakdown || []
          this.overdueNextResponse = data.overdue_next_response
          this.overdueResolution = data.overdue_resolution
          this.lastRefreshed = moment(data.fetched_at).format('h:mm A')
        }).catch(() => {
          notify.error('There was an issue retrieving tickets from Freshdesk.')
        }).finally(() => {
          this.loading = false
        })
      },

      refreshData () {
        this.fetchData({ refresh: true })
      },

      statusLabel (status) {
        return STATUS_LABELS[status] || `Status ${status}`
      },

      statusBadgeStyle (status) {
        const color = STATUS_COLORS[status] || '#495057'
        return { background: hexToRgba(color, 0.10), color }
      },

      ctbSort (key) {
        if (this.clientSortKey === key) {
          this.clientSortDir *= -1
        } else {
          this.clientSortKey = key
          this.clientSortDir = -1
        }
      },


      priorityLabel (priority) {
        return PRIORITY_LABELS[priority] || '—'
      },

      priorityClass (priority) {
        return PRIORITY_CLASSES[priority] || 'status-mute'
      },

      priorityBadgeClass (priority) {
        const classes = { 1: 'bg-secondary', 2: 'bg-info', 3: 'bg-warning', 4: 'bg-danger' }
        return classes[priority] || 'bg-secondary'
      },
    }
  }
</script>

<style scoped>
.ticket-id {
  color: #0d6efd;
  font-weight: 600;
  white-space: nowrap;
}

/* Client Ticket Breakdown card */
.ctb {
  border-radius: 8px;
  overflow: hidden;
}

.ctb-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 12px;
}

.ctb-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.ctb-legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #767676;
}
.ctb-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ctb-search-wrap {
  position: relative;
}
.ctb-search-wrap svg {
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.ctb-search {
  height: 32px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0 12px 0 28px;
  font-size: 12px;
  color: #1f1f1f;
  outline: none;
  width: 180px;
}
.ctb-search:focus { border-color: #0360dc; }

.ctb-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.ctb-table {
  width: 100%;
  border-collapse: collapse;
}
.ctb-table thead tr { border-bottom: 1px solid #e9ecef; }
.ctb-table thead th {
  padding: 11px 12px;
  font-size: 11px;
  font-weight: 700;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}
.ctb-table thead th:first-child { padding-left: 20px; }
.ctb-table thead th:last-child  { padding-right: 20px; }

.sort-icon { display: inline-block; margin-left: 4px; opacity: 0.4; font-size: 9px; }
.ctb-table thead th.sorted .sort-icon { opacity: 1; color: #0360dc; }

.ctb-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.1s;
}
.ctb-table tbody tr:last-child { border-bottom: none; }
.ctb-table tbody tr:hover { background: #f6f7f8; }

.ctb-table td {
  padding: 13px 12px;
  font-size: 13px;
  color: #1f1f1f;
  vertical-align: middle;
  white-space: nowrap;
}
.ctb-table td:first-child { padding-left: 20px; }
.ctb-table td:last-child  { padding-right: 20px; }

.ctb-client-name {
  font-weight: 600;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ctb-total { font-weight: 700; }

.ctb-bar {
  height: 4px;
  border-radius: 100px;
  overflow: hidden;
  display: flex;
  background: #f0f0f0;
  min-width: 80px;
  width: 100%;
}
.ctb-bar-seg { height: 100%; }

.ctb-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 22px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  padding: 0 8px;
}
.ctb-badge-nil { color: #dee2e6; }

.ctb-empty {
  text-align: center;
  padding: 40px;
  color: #767676;
  font-size: 13px;
}
</style>
